import React, { useState, useEffect } from 'react';
import './RequestEstimate.css';

// Import necessary components from the components directory
import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaBuilding, FaCogs, FaCalendar, FaDollarSign, FaChartBar, FaComment, FaAngleDown } from 'react-icons/fa';
import axios from "axios";

// API endpoint for submitting the form
const API_PATH = 'https://aibyte-react.asdesignsgalaxy.com/api/phpmailer/estimate.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const RequestEstimate = () => {

  // State to manage form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    number: '',
    service: '',
    timeline: '',
    budget: '',
    businessSize: '',
    description: ''
  });

  const [mailSent, setMailSent] = useState(false); // State to track if the email was sent successfully
  const [error, setError] = useState(''); // State to store error messages
  const [isSubmitting, setIsSubmitting] = useState(false); // State to track if the form is being submitted

  // Effect to reset the mailSent state after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  /**
   * Handles form submission.
   * Validates form data and sends it to the server.
   */
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.company.trim()) validationErrors.push('Company name is required');
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.service) validationErrors.push('Service is required');
      if (!formData.timeline) validationErrors.push('Project timeline is required');
      if (!formData.budget) validationErrors.push('Budget range is required');
      if (!formData.businessSize) validationErrors.push('Business size is required');
      if (!formData.description.trim()) validationErrors.push('Project description is required');

      // If there are validation errors, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Send form data to the server
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle server response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // Reset form and show success message
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        number: '',
        service: '',
        timeline: '',
        budget: '',
        businessSize: '',
        description: ''
      });
    } catch (err) {
      // Handle errors
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles input changes and updates the form data state.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
 
  return (
    <section className="custom-form request-estimate linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Request Estimate"} subTitle={"Get a Quote"} linear />

            <div className="input-box">

              {/* Full Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Company Name Input Field */}
              <div className="input-field">
                <label htmlFor="company"><FaBuilding /></label>
                <input
                  type="text"
                  name="company"
                  className="box"
                  id="company"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="number"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Service Interested In */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="service"><FaCogs /></label>
                  <select
                    className="select-box"
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Interested In</option>
                    <option value="ai-solutions">AI-Powered Solution</option>
                    <option value="it-consulting">IT Consulting</option>
                    <option value="cloud-integration">Cloud Integration</option>
                    <option value="code-development">Code Development</option>
                    <option value="cyber-protection">Cyber Protection</option>
                    <option value="data-analytics">Big Data Analytics</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              {/* Project Timeline Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="timeline"><FaCalendar /></label>
                  <select
                    className="select-box"
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Project Timeline</option>
                    <option value="less-than-1-month">Less than 1 month</option>
                    <option value="1-3-months">1-3 months</option>
                    <option value="3-6-months">3-6 months</option>
                    <option value="6-12-months">6-12 months</option>
                    <option value="more-than-1-year">More than 1 year</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">

              {/* Budget Range */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="budget"><FaDollarSign /></label>
                  <select
                    className="select-box"
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Budget Range</option>
                    <option value="below-5k">Below $5,000</option>
                    <option value="5k-10k">$5,000 - $10,000</option>
                    <option value="10k-20k">$10,000 - $20,000</option>
                    <option value="20k-plus">$20,000+</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              {/* Business Size Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="businessSize"><FaChartBar /></label>
                  <select
                    className="select-box"
                    id="businessSize"
                    name="businessSize"
                    value={formData.businessSize}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Business Size</option>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            {/* Project Description Text Area */}
            <div className="input-field text-area">
              <label htmlFor="description"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="description"
                className="box"
                id="description"
                placeholder="Project Description"
                value={formData.description}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Estimate'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Request Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default RequestEstimate;